import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, AlertTriangle, Loader2, CheckCircle2, XCircle, AlertCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CAN_MANAGE = ["super_admin", "hr", "manager", "team_lead"];

const WEEK_STATUSES = [
  { value: "on_track", label: "On track", color: "emerald" },
  { value: "needs_attention", label: "Needs attention", color: "amber" },
  { value: "off_track", label: "Off track", color: "rose" },
];

const OUTCOMES = [
  { value: "passed", label: "Passed — close PIP" },
  { value: "extended", label: "Extended" },
  { value: "terminated", label: "Terminated" },
];

export default function Pip() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);

  const [pips, setPips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [refresh, setRefresh] = useState(0);

  const [initOpen, setInitOpen] = useState(false);
  const [initForm, setInitForm] = useState({ employee_id: "", reason: "", expected_outcome: "", start_date: new Date().toISOString().slice(0, 10) });
  const [initSubmitting, setInitSubmitting] = useState(false);

  const [activePip, setActivePip] = useState(null);
  const [weekForm, setWeekForm] = useState({ week: 1, status: "on_track", comment: "" });
  const [weekSaving, setWeekSaving] = useState(false);

  const [closeOpen, setCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ outcome: "passed", note: "" });

  useEffect(() => { api.get("/pips").then((r) => setPips(r.data || [])); }, [refresh]);
  useEffect(() => {
    if (!canManage || employees.length > 0) return;
    // Team Leads pick from their own coders only; HR / Manager / Admin see everyone.
    if (user?.role === "team_lead") {
      api.get("/my-team")
        .then((r) => setEmployees(r.data?.direct || []))
        .catch(() => setEmployees([]));
    } else {
      api.get("/employees").then((r) => setEmployees(r.data || []));
    }
  }, [canManage, employees.length, user?.role]);

  const submitInit = async () => {
    if (!initForm.employee_id) { toast.error("Select an employee"); return; }
    if (!initForm.reason || initForm.reason.trim().length < 3) { toast.error("Reason is required"); return; }
    setInitSubmitting(true);
    try {
      await api.post("/pips/initiate", initForm);
      toast.success("PIP initiated · employee notified");
      setInitOpen(false);
      setInitForm({ employee_id: "", reason: "", expected_outcome: "", start_date: new Date().toISOString().slice(0, 10) });
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setInitSubmitting(false); }
  };

  const saveWeek = async () => {
    if (!activePip) return;
    if (weekForm.comment.trim().length < 3) { toast.error("Please add a comment"); return; }
    setWeekSaving(true);
    try {
      const { data } = await api.post(`/pips/${activePip.id}/week-update`, weekForm);
      setActivePip(data);
      toast.success(`Week ${weekForm.week} updated`);
      setRefresh((v) => v + 1);
      setWeekForm({ ...weekForm, comment: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setWeekSaving(false); }
  };

  const closePip = async () => {
    if (!activePip) return;
    try {
      await api.post(`/pips/${activePip.id}/close`, closeForm);
      toast.success("PIP closed");
      setCloseOpen(false);
      setActivePip(null);
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="pip-page">
      <PageHeader
        title="Performance Improvement Plan"
        subtitle="Place an employee on a 4-week PIP. Record weekly status comments and close with an outcome."
        action={
          canManage ? (
            <Button data-testid="initiate-pip" onClick={() => setInitOpen(true)} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
              <Plus className="h-4 w-4 mr-1.5" /> Initiate PIP
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pips.length === 0 ? (
          <Card className="hr-card p-10 text-center text-sm text-gray-500 md:col-span-3">
            No PIPs on record. Click <b>Initiate PIP</b> to start a 4-week plan.
          </Card>
        ) : pips.map((p) => {
          const completed = (p.weeks || []).filter((w) => w.status !== "pending").length;
          return (
            <Card key={p.id} className="hr-card p-5 cursor-pointer hover:shadow-md transition-shadow" data-testid={`pip-${p.id}`} onClick={() => { setActivePip(p); setWeekForm({ week: 1, status: "on_track", comment: "" }); }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-semibold text-[#0F172A]">{p.employee_name}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{p.employee_id} · {p.department}</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${
                  p.status === "active" ? "bg-amber-100 text-amber-800"
                  : p.outcome === "passed" ? "bg-emerald-100 text-emerald-800"
                  : p.outcome === "terminated" ? "bg-rose-100 text-rose-800"
                  : "bg-gray-100 text-gray-700"
                }`}>{p.status === "closed" ? p.outcome : p.status}</span>
              </div>
              <div className="text-xs text-gray-600 mt-2 line-clamp-2">{p.reason}</div>
              <div className="text-[11px] text-gray-500 mt-1">Started {p.start_date}</div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
                <span>Week progress</span><span className="font-mono">{completed}/4</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-[#1E88E5]" style={{ width: `${(completed / 4) * 100}%` }} />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Initiate PIP */}
      <Dialog open={initOpen} onOpenChange={setInitOpen}>
        <DialogContent className="max-w-lg" data-testid="initiate-pip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#F26B3A]" /> Initiate PIP</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Employee</Label>
              <Select value={initForm.employee_id} onValueChange={(v) => setInitForm({ ...initForm, employee_id: v })}>
                <SelectTrigger data-testid="pip-emp-select" className="mt-1.5"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id || e.id} value={e.employee_id || ""} data-testid={`pip-emp-${e.employee_id}`}>
                      {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span> · {e.designation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Start date</Label>
              <Input data-testid="pip-start" type="date" className="mt-1.5" value={initForm.start_date} onChange={(e) => setInitForm({ ...initForm, start_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Reason</Label>
              <Textarea rows={2} data-testid="pip-reason" className="mt-1.5" value={initForm.reason} onChange={(e) => setInitForm({ ...initForm, reason: e.target.value })} placeholder="e.g. Coding accuracy below 90% for two consecutive months" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Expected outcome</Label>
              <Textarea rows={2} data-testid="pip-outcome" className="mt-1.5" value={initForm.expected_outcome} onChange={(e) => setInitForm({ ...initForm, expected_outcome: e.target.value })} placeholder="e.g. Achieve and sustain ≥ 95% accuracy" />
            </div>
            <div className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {user?.role === "team_lead"
                ? "Only your direct coders are listed. The employee will be notified by email and you must add weekly status comments for 4 weeks."
                : "The employee will be notified by email. Weekly status comments must be added for 4 weeks."}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInitOpen(false)}>Cancel</Button>
            <Button data-testid="pip-init-submit" onClick={submitInit} disabled={initSubmitting} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {initSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Initiate PIP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / weekly updates */}
      <Dialog open={!!activePip && !closeOpen} onOpenChange={(v) => { if (!v) setActivePip(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pip-detail-dialog">
          <DialogHeader>
            <DialogTitle>{activePip?.employee_name} ({activePip?.employee_id}) · PIP</DialogTitle>
          </DialogHeader>
          {activePip && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-3 gap-3 pb-3 border-b border-gray-100">
                <Info label="Started" value={activePip.start_date} />
                <Info label="Status" value={activePip.status === "closed" ? activePip.outcome : activePip.status} />
                <Info label="Initiated by" value={activePip.initiated_by} />
                <div className="col-span-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">Reason</div>
                  <div className="text-gray-800">{activePip.reason}</div>
                </div>
                <div className="col-span-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">Expected outcome</div>
                  <div className="text-gray-800">{activePip.expected_outcome || "—"}</div>
                </div>
              </div>

              <div className="space-y-3">
                {(activePip.weeks || []).map((w) => {
                  const statusMeta = WEEK_STATUSES.find((s) => s.value === w.status);
                  const colorMap = { emerald: "bg-emerald-50 border-emerald-200 text-emerald-800", amber: "bg-amber-50 border-amber-200 text-amber-800", rose: "bg-rose-50 border-rose-200 text-rose-800" };
                  return (
                    <div key={w.week} className={`rounded-md border p-3 ${statusMeta ? colorMap[statusMeta.color] : "bg-gray-50 border-gray-200"}`} data-testid={`pip-week-${w.week}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-medium">Week {w.week}</div>
                        <div className="text-[11px] capitalize">{w.status === "pending" ? "— pending —" : statusMeta?.label}</div>
                      </div>
                      {w.comment ? (
                        <div className="text-xs mt-1">{w.comment}</div>
                      ) : (
                        <div className="text-[11px] italic mt-1 opacity-60">No comment yet</div>
                      )}
                      {w.updated_at && <div className="text-[10px] mt-1 opacity-70">Updated {w.updated_at.slice(0, 10)} by {w.updated_by}</div>}
                    </div>
                  );
                })}
              </div>

              {canManage && activePip.status === "active" && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Add weekly comment</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Week</Label>
                      <Select value={String(weekForm.week)} onValueChange={(v) => setWeekForm({ ...weekForm, week: Number(v) })}>
                        <SelectTrigger data-testid="pip-week-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>{[1,2,3,4].map((n) => <SelectItem key={n} value={String(n)} data-testid={`pip-week-opt-${n}`}>Week {n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-gray-500">Status</Label>
                      <Select value={weekForm.status} onValueChange={(v) => setWeekForm({ ...weekForm, status: v })}>
                        <SelectTrigger data-testid="pip-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {WEEK_STATUSES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`pip-status-${s.value}`}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Textarea rows={3} data-testid="pip-comment" className="mt-3" value={weekForm.comment} onChange={(e) => setWeekForm({ ...weekForm, comment: e.target.value })} placeholder="What did the employee work on this week? Outcomes, blockers, next steps." />
                  <div className="mt-3 flex justify-between">
                    <Button data-testid="pip-close-open" variant="outline" onClick={() => setCloseOpen(true)}>Close PIP</Button>
                    <Button data-testid="pip-week-save" onClick={saveWeek} disabled={weekSaving} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
                      {weekSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save week</>}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Close PIP */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-md" data-testid="close-pip-dialog">
          <DialogHeader><DialogTitle>Close PIP</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Outcome</Label>
              <Select value={closeForm.outcome} onValueChange={(v) => setCloseForm({ ...closeForm, outcome: v })}>
                <SelectTrigger data-testid="pip-outcome-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Note (optional)</Label>
              <Textarea rows={3} data-testid="pip-close-note" className="mt-1.5" value={closeForm.note} onChange={(e) => setCloseForm({ ...closeForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button data-testid="pip-close-confirm" onClick={closePip} className="bg-[#0F172A] hover:bg-[#1e293b]">Close PIP</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-gray-800 text-sm capitalize">{value || "—"}</div>
    </div>
  );
}
